import { useMutation } from 'stimulus-use';
import { Controller } from '@hotwired/stimulus';
import { CalculateLeadScore } from '../shared/utils/lead-score';

export default class extends Controller {
    static get values() {
        return {
            name: String,
            type: String,
            placeholder: String,
            formId: String,
        }
    }

    static get targets() {
        return [
            'input',
        ]
    }

    async labelValue() {
        const label = this.element.querySelector('label');
        let labelValue;

        if(this.typeValue == 'select-one') {
            labelValue = this.element.querySelector('option[disabled]');
            labelValue.innerHTML = labelValue.innerHTML.replace('*','');
        } else {
            labelValue = this.element.querySelector('span');
        }

        return {
            label,
            labelValue,
        }
    }

    async formatInput() {
        if (this.typeValue == 'hidden') {
            return null;
        }

        const { label, labelValue } = await this.labelValue();

        switch(true) {
            case !label:
                return null;
            case ![ null, '' ].includes(this.placeholderValue):
                label.classList.add('hs-label-display');
                label.innerHTML = this.placeholderValue;
                this.inputTarget.placeholder = this.placeholderValue;
                return;
            default:
                const placeholder_value = labelValue.innerHTML.replace('*', '');
                label.classList.add('hs-label-display');
                label.innerHTML = placeholder_value;
                this.placeholderValue = placeholder_value;
                this.inputTarget.placeholder = placeholder_value;
                return;
        }

    }

    placeholderValueChanged() {
        if (!this.placeholderValue || this.placeholderValue.includes('*')) {
            this.placeholderValue = this.placeholderValue.replace('*', '');
        }
    }

    blacklist(type) {
        const blacklist = ['button', 'radial', 'date', 'hidden', 'radio', 'range', 'submit', 'time'].includes(type);

        return blacklist;
    }

    formIdValueChanged() {
        if (!this.formIdValue) {
            return null;
        }

        return this.element.setAttribute('data-action', `hs-input-visibility-update-${this.formIdValue}@window->hs-input-controller#handleVisibility hs-input-validation-${this.formIdValue}@window->hs-input-controller#validationRequest`);
    }

    validateWebsite() {
        const valid_website = new RegExp(/^(http:\/\/|https:\/\/)?([a-zA-Z0-9\/\-\?\=\_\%\~\.]+)?[a-zA-Z0-9\/\-\?\=\_\%\~.]*\.([a-zA-Z0-9\/\-\?\=\_\%\~\&]+)?$/i);
        const website_blacklist_contains = new RegExp(/(facebook|maps\.app|goo\.gl)/i);
        const website_blacklist_equals = new RegExp(/^(http:\/\/|https:\/\/|http|https|http:|https:)?(na\.com|hover\.com|epik\.com|web\.com|name\.com|aruba\.it|hover\.com|ionos\.com|register\.com|domain\.com|enom\.com|reg\.ru|nope\.com|none\.com|onamae\.com|nill\.com|no\.com)$/i);
        const value = this.inputTarget.value;
        const blacklist_contains = value ? value.match(website_blacklist_contains) : null;
        const blacklist_equals = value ? value.match(website_blacklist_equals) : null;
        const validation = value ? value.match(valid_website) : null;
        const labelValue = this.element.querySelector('.hs-label-display');

        if (validation === null || blacklist_contains !== null || blacklist_equals !== null) {
            this.element.classList.add('hs-input--error', 'website-error');
            this.inputTarget.classList.add('error');
            labelValue.innerHTML = 'Please enter a valid website';
            return window.dispatchEvent(new CustomEvent(`lock-form-${this.formIdValue}`));
        } else {
            this.element.classList.remove('hs-input--error', 'website-error');
            this.inputTarget.classList.remove('error');
            labelValue.innerHTML = this.placeholderValue;

            return window.dispatchEvent(new CustomEvent(`unlock-form-${this.formIdValue}`));
        }
    }

    validationRequest(event) {
        if (!this.hasInputTarget) {
            return null;
        }

        const fields = event?.detail?.fields;

        if (!Array.isArray(fields) || !fields.includes(this.nameValue)) {
            return null;
        }

        this.inputTarget.focus();
        this.inputTarget.blur();

        if (this.nameValue == 'website') {
            this.validateWebsite();
        }

        return window.dispatchEvent(new CustomEvent(`hs-input-validation-response-${this.formIdValue}`, {
            detail: {
                field: this.nameValue,
                target: this.inputTarget,
            },
        }));
    }

    async connect() {
        useMutation(this, { childList: true, subtree: true });
        const input = this.element.querySelector('input, select, textarea');
        const phoneElement = this.element.querySelector('input[type="tel"]');
        const blacklist = this.blacklist(input.type);

        if (blacklist) {
            return null;
        }

        switch(true) {
            case phoneElement !== null:
                const [
                    { default: intlTelInput },
                ] = await Promise.all([
                    import(/* webpackChunkName: "vendor-intl-tel" */ 'intl-tel-input'),
                ]);

                this.nameValue = 'Phone';
                this.typeValue = 'tel';
                this.placeholderValue = 'Phone';
                this.phoneElement = phoneElement;

                this.phoneElement.setAttribute('data-action', `focus->hs-input-controller#handleFocus input->hs-input-controller#handleBlur blur->hs-input-controller#handleBlur hs-input-visibility-update-${this.formIdValue}@window->hs-input-controller#handleVisibility`);
                this.phoneElement.setAttribute('data-hs-input-controller-target', 'input');
                this.phoneElement.addEventListener('countrychange', this.handleCountryChange.bind(this));
                this.element.classList.add('hs-field-phone');

                if (typeof intlTelInput === 'undefined' || !intlTelInput) {
                    return null;
                }

                this.iti = intlTelInput(this.phoneElement, {
                    loadUtils: () => Promise.resolve(import(/* webpackChunkName: "vendor-intl-tel" */ 'intl-tel-input/utils')),
                    initialCountry: 'us',
                    nationalMode: false,
                    separateDialCode: false,
                    formatAsYouType: true,
                    formatOnDisplay: true,
                });

                this.formatInput();
                return;
            case input.name == 'website':
                input.setAttribute('data-hs-input-controller-target', 'input');
                input.setAttribute('data-action', `focus->hs-input-controller#handleFocus input->hs-input-controller#handleBlur blur->hs-input-controller#handleBlur hs-input-visibility-update-${this.formIdValue}@window->hs-input-controller#handleVisibility`);
                this.nameValue = input.name;
                this.typeValue = input.type;
                this.placeholderValue = input.placeholder;

                this.formatInput();
                return;
            case input.type == 'checkbox':
                input.setAttribute('data-hs-input-controller-target', 'input');
                input.setAttribute('data-action', `focus->hs-input-controller#handleFocus input->hs-input-controller#handleBlur blur->hs-input-controller#handleBlur hs-input-visibility-update-${this.formIdValue}@window->hs-input-controller#handleVisibility`);
                this.nameValue = input.name;
                this.typeValue = input.type;
                this.placeholderValue = input.placeholder;

                return;
            default:
                input.setAttribute('data-hs-input-controller-target', 'input');
                input.setAttribute('data-action', `focus->hs-input-controller#handleFocus input->hs-input-controller#handleBlur blur->hs-input-controller#handleBlur hs-input-visibility-update-${this.formIdValue}@window->hs-input-controller#handleVisibility`);
                this.nameValue = input.name;
                this.typeValue = input.type;
                this.placeholderValue = input.placeholder;

                this.formatInput();
                return;
        }
    }

    handleCountryChange() {
        const { iso2 } = this.iti.getSelectedCountryData();
        const countryElement = this.element.querySelector('select');

        if (!countryElement) {
            return null;
        }

        countryElement.value = iso2.toUpperCase();
        const selection = new Event('change', { bubbles: true });

        return countryElement.dispatchEvent(selection);
    }

    handleVisibility(event) {
        const {
            detail: {
                active_fields,
                position,
            },
        } = event;

        switch(true) {
            case position == 0 && active_fields.includes(this.nameValue):
                return null;
            case position == 0 && !active_fields.includes(this.nameValue):
                this.element.classList.add('hs-input--hidden');
                return;
            case active_fields.includes(this.nameValue):
                this.element.classList.add('hs-input--in');
                this.element.classList.remove('hs-input--hidden');
                return;
            case !this.element.classList.contains('hs-input--hidden'):
                this.element.classList.remove('hs-input--in');
                this.element.classList.add('hs-input--out');
                return;
        }
    }

    handleFocus(event) {
        this.element.classList.add('hs-input--focused');
        this.inputTarget.placeholder = '';
    }

    handleBlur(event) {
        this.element.classList.remove('hs-input--focused');
        this.inputTarget.placeholder = this.placeholderValue;

        if (this.nameValue == 'website') {
            this.validateWebsite();
        }

        window.dispatchEvent(new CustomEvent(`field-update-${this.formIdValue}`, {
            detail: {
                field: this.nameValue,
                value: event.target.value,
            },
        }));
    }

    mutate() {
        const error_messages = this.element.querySelector('.hs-error-msgs');

        switch(true) {
            case this.inputTarget.classList.contains('error') && this.typeValue !== 'checkbox':
            case error_messages !== null:
                this.element.classList.add('hs-input--error');
                this.inputTarget.classList.add('error');
                return;
            default:
                this.element.classList.remove('hs-input--error');
                this.inputTarget.classList.remove('error');
                return;
        }
    }

    disconnect() {
        if(this.phoneElement) {
            this.phoneElement.removeEventListener('countrychange', this.handleCountryChange);
            this.phoneElement = null;
        }
    }
}
